import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, MapPin, Building2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "Restaurant",
  "Cafe",
  "Bar",
  "Retail",
  "Salon/Barbershop",
  "Grocery",
  "Convenience Store",
  "Pharmacy",
  "Gym",
  "Arts Venue",
  "Public Services",
  "Bank",
  "Other"
];

export const AddBusinessDialog = ({ 
  open, 
  onOpenChange, 
  neighborhoods, 
  neighborhoodCoords,
  onSuccess 
}) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    neighborhood: '',
    category: '',
    description: '',
    latitude: '',
    longitude: ''
  });

  // Auto-fill coordinates when neighborhood changes
  useEffect(() => {
    if (formData.neighborhood && neighborhoodCoords[formData.neighborhood]) {
      const coords = neighborhoodCoords[formData.neighborhood];
      // Add small random offset to avoid exact overlap
      const latOffset = (Math.random() - 0.5) * 0.01;
      const lngOffset = (Math.random() - 0.5) * 0.01;
      setFormData(prev => ({
        ...prev,
        latitude: (coords.lat + latOffset).toFixed(6),
        longitude: (coords.lng + lngOffset).toFixed(6)
      }));
    }
  }, [formData.neighborhood, neighborhoodCoords]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      toast.error('Please enter the business name');
      return;
    }
    if (!formData.address.trim()) {
      toast.error('Please enter the address');
      return;
    }
    if (!formData.neighborhood) {
      toast.error('Please select a neighborhood');
      return;
    }
    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/businesses`,
        {
          name: formData.name,
          address: formData.address,
          neighborhood: formData.neighborhood,
          category: formData.category,
          description: formData.description,
          latitude: parseFloat(formData.latitude),
          longitude: parseFloat(formData.longitude)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Business added successfully!');
      onSuccess(response.data);
      
      // Reset form
      setFormData({
        name: '',
        address: '',
        neighborhood: '',
        category: '',
        description: '',
        latitude: '',
        longitude: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add business');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" data-testid="add-business-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-display">
            <Building2 className="w-5 h-5 text-primary" />
            Add a Business
          </DialogTitle>
          <DialogDescription>
            Help others by adding a business to our community map.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="name">Business Name *</Label>
            <Input
              id="name"
              placeholder="e.g., Joe's Coffee Shop"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              data-testid="business-name-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="address">Address *</Label>
            <Input
              id="address"
              placeholder="e.g., 123 Bedford Ave, Brooklyn"
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              data-testid="business-address-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Neighborhood *</Label>
              <Select
                value={formData.neighborhood}
                onValueChange={(value) => handleChange('neighborhood', value)}
              >
                <SelectTrigger data-testid="neighborhood-select">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {neighborhoods.map((hood) => (
                    <SelectItem key={hood} value={hood}>
                      {hood}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => handleChange('category', value)}
              >
                <SelectTrigger data-testid="category-select">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              placeholder="Brief description of the business..."
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              className="resize-none"
              data-testid="business-description-input"
            />
          </div>
          
          {/* Hidden coordinates (auto-filled based on neighborhood) */}
          <input type="hidden" value={formData.latitude} />
          <input type="hidden" value={formData.longitude} />
          
          {formData.latitude && formData.longitude && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 p-2 rounded">
              <MapPin className="w-3 h-3" />
              <span>Location: {formData.latitude}, {formData.longitude}</span>
            </div>
          )}
          
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1 rounded-full"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 rounded-full bg-primary"
              disabled={loading}
              data-testid="submit-business-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Business'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
